import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, ShieldAlert,
  FolderOpen, CircleDollarSign, Handshake, Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FilterBar } from '../../components/ui/FilterBar';
import { TablePagination } from '../../components/ui/Table';
import api from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { claimType, fmtEur } from './utils';
import { ClaimsKpiCard }  from './components/ClaimsKpiCard';
import { ClaimRow }       from './components/ClaimRow';
import { ClaimEmptyState } from './components/ClaimEmptyState';
import { ClaimDrawer }    from './components/ClaimDrawer';

const PER_PAGE = 10;

export default function InsuranceClaimsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language; // eslint-disable-line no-unused-vars
  const { user, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !hasPermission('view_claims')) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, hasPermission, navigate]);

  const [claims, setClaims]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [stats, setStats]         = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError]         = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch]       = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [page, setPage]           = useState(1);
  const [selectedClaim, setSelectedClaim] = useState(null);

  const isAdmin = user?.company?.type === 'ADMIN';

  const loadMeta = useCallback(() => {
    if (!isAdmin) return;
    api.post('/companies/get-all?skip=0&limit=500', { type: 'CLIENT' })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.companies ?? []);
        setCompanies(list);
      })
      .catch(() => {});
  }, [isAdmin]);

  const loadStats = useCallback(() => {
    setStatsLoading(true);
    api.get('/claims/stats')
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  const loadClaims = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    api.get(`/claims?${params}`)
      .then((res) => { setClaims(res.data.data ?? []); setTotal(res.data.total ?? 0); })
      .catch((err) => setError(err.message || t('common.error_occurred')))
      .finally(() => setLoading(false));
  }, [typeFilter, debouncedSearch, page, t]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadClaims(); }, [loadClaims]);

  function refresh() { loadMeta(); loadStats(); loadClaims(); }

  const companyMap = useMemo(() => {
    const m = {};
    companies.forEach((c) => { m[String(c._id)] = c.name; });
    return m;
  }, [companies]);

  const enriched = useMemo(() =>
    claims.map((c) => ({
      ...c,
      companyName: companyMap[String(c.clientId)] || user?.company?.name || '—',
      claimType:   claimType(c.reference),
    })),
    [claims, companyMap, user],
  );

  const counts = stats?.counts ?? { all: 0, litige: 0, sinistre: 0 };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('claims.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t('claims.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
          >
            <RefreshCw size={13} strokeWidth={1.75} className={loading ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
          <button className="h-8 px-3 flex items-center gap-1.5 rounded-lg bg-[#0F3D5C] text-white text-[12px] font-semibold hover:opacity-90 transition">
            <ShieldAlert size={13} strokeWidth={1.75} />
            {t('claims.btn_declare')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
          <span>{error}</span>
          <button onClick={refresh} className="font-medium underline text-[12px] text-rose-700">
            {t('common.retry')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-4 gap-4">
        <ClaimsKpiCard
          label={t('claims.kpi_open')}
          value={statsLoading ? '—' : stats?.open}
          sub={stats?.open > 0 ? t('claims.kpi_open_sub', { count: counts.sinistre }) : undefined}
          Icon={FolderOpen}
        />
        <ClaimsKpiCard
          label={t('claims.kpi_total_claimed')}
          value={statsLoading ? '—' : fmtEur(stats?.totalClaimed)}
          sub={t('claims.kpi_total_claimed_sub', { count: counts.all })}
          Icon={CircleDollarSign}
        />
        <ClaimsKpiCard
          label={t('claims.kpi_indemnized_ytd')}
          value={statsLoading ? '—' : fmtEur(stats?.indemnizedYtd)}
          sub={stats?.acceptRate != null ? t('claims.kpi_accept_rate', { rate: stats.acceptRate }) : undefined}
          subGreen={stats?.acceptRate != null}
          Icon={Handshake}
        />
        <ClaimsKpiCard
          label={t('claims.kpi_resolve_days')}
          value={statsLoading ? '—' : (stats?.resolveDays != null ? `${stats.resolveDays} j` : '—')}
          Icon={Clock}
        />
      </div>

      <FilterBar
        groups={[{
          value: typeFilter,
          onChange: (k) => { setTypeFilter(k); setPage(1); },
          tabs: [
            { key: 'all',      label: t('common.all'),           count: counts.all      },
            { key: 'litige',   label: t('claims.tab_litiges'),   count: counts.litige   },
            { key: 'sinistre', label: t('claims.tab_sinistres'), count: counts.sinistre },
          ],
        }]}
        search={search}
        onSearch={(e) => { setSearch(e.target.value); setPage(1); }}
        searchPlaceholder={t('claims.search_placeholder')}
      />

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl py-12 flex items-center justify-center">
          <RefreshCw size={18} strokeWidth={1.75} className="animate-spin text-slate-400" />
        </div>
      ) : enriched.length === 0 ? (
        <ClaimEmptyState />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="divide-y divide-slate-100">
            {enriched.map((claim) => (
              <ClaimRow key={String(claim._id)} claim={claim} onClick={() => setSelectedClaim(claim)} />
            ))}
          </div>
          <TablePagination
            page={page}
            total={total}
            perPage={PER_PAGE}
            onPrev={() => setPage((p) => p - 1)}
            onNext={() => setPage((p) => p + 1)}
          />
        </div>
      )}

      <ClaimDrawer claim={selectedClaim} onClose={() => setSelectedClaim(null)} />
    </div>
  );
}
